import { useEffect, useCallback, useState, useReducer } from "react";
import { useLocation } from "react-router-dom";

import { createNewUser, setRolesOfUser } from "services/authentication";
import { useAuthContext } from "context/AuthContext";

import { useLogManager } from "hooks/useLogManager";
import { useDocument } from "hooks/useDocument";

import { useAbac } from "react-abac";
import { Permission } from "models/abac";

import initialValues from "pages/settings/user/new/schemas/initialValues";
import validations from "pages/settings/user/new/schemas/validations";

import generator from "generate-password-browser";

import { sendSignupEmail } from "services/callable";

import crypto from "crypto-browserify";

const initialState = {
  data: initialValues,
  isPending: false,
  error: null,
  success: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "DISMISS":
      return {
        isPending: false,
        data: initialValues,
        success: null,
        error: null,
      };
    case "IS_PENDING":
      return {
        isPending: true,
        data: initialValues,
        success: null,
        error: null,
      };
    case "INITIAL_USER":
      return {
        isPending: false,
        data: action.payload,
        success: null,
        error: null,
      };
    case "RETRIEVED_USER":
      return {
        isPending: false,
        data: action.payload,
        success: null,
        error: null,
      };
    case "CREATED_USER":
      return {
        isPending: false,
        data: action.payload,
        success: `Successfully created the user, ${action.payload.displayName}.`,
        error: null,
      };
    case "ERROR":
      return {
        isPending: false,
        data: initialValues,
        success: null,
        error: action.error,
      };
    default:
      return state;
  }
};

export const useSettingsManager = () => {
  const [response, dispatch] = useReducer(reducer, initialState);
  const [isUnmounted, setIsUnmounted] = useState(false);

  const { user } = useAuthContext();

  const { userHasPermissions } = useAbac();

  const { logUserActivity } = useLogManager();
  const { createDoc, updateDoc, serverTimestamp } = useDocument();

  const { pathname } = useLocation();

  const dispatchIfNotUnmounted = useCallback(
    (action) => {
      if (!isUnmounted) {
        dispatch(action);
      }
    },
    [isUnmounted]
  );

  const dispatchDismiss = useCallback(
    () => dispatchIfNotUnmounted({ type: "DISMISS" }),
    [dispatchIfNotUnmounted]
  );

  const dispatchError = useCallback(
    (err) => {
      console.error(err);
      if (
        !["PermissionDeniedError", "OperationInvalidError"].includes(err.name)
      ) {
        err.message = "The operation couldn't be completed";
        err.name = "OperationIncompleteError";
        // TODO: send error stack to server
      }
      dispatchIfNotUnmounted({
        type: "ERROR",
        error: err,
      });
    },
    [dispatchIfNotUnmounted]
  );

  // TODO: Refactor to DAO Layer
  const toPersistenceValue = useCallback(
    (document) => {
      try {
        return document;
      } catch (err) {
        dispatchError(err);
      }
    },
    [dispatchError]
  );

  // TODO: Refactor to DAO Layer
  const toPresentationValue = useCallback(
    (data) => {
      try {
        // nothing to convert at this moment

        return data;
      } catch (err) {
        dispatchError(err);
      }
    },
    [dispatchError]
  );

  const validateOperation = useCallback(async () => {
    try {
      dispatchIfNotUnmounted({ type: "IS_PENDING" });
      let operationInvalidError = new Error(
        "Invalid Operation. You are not allowed to carry out this activity."
      );
      operationInvalidError.name = "OperationInvalidError";

      if (!pathname.includes("/settings/user")) {
        throw operationInvalidError;
      }

      dispatchIfNotUnmounted({
        type: "INITIAL_USER",
        payload: initialValues,
      });
    } catch (err) {
      dispatchError(err);
    }
  }, [dispatchIfNotUnmounted, pathname, dispatchError]);

  useEffect(() => {
    try {
      validateOperation();
    } catch (err) {
      dispatchError(err);
    }
    return () => {
      setIsUnmounted(true);
    };
  }, [dispatchError, validateOperation]);

  let modeTitle = "New User";
  let modeSubmit = "Create";
  let modeFieldDisabled = false;
  let modePermission = Permission.MANAGE_ADD_USER;
  let modeValidation = validations;

  const submitNew = async (values, continueURL) => {
    try {
      dispatchIfNotUnmounted({ type: "IS_PENDING" });
      if (userHasPermissions([Permission.CREATE_USER])) {
        values = toPersistenceValue(values);

        const password = generator.generate({
          length: 15,
          numbers: true,
          symbols: true,
          lowercase: true,
          uppercase: true,
          strict: true,
        });

        const result = await createNewUser({
          email: values.email,
          password: password,
          emailVerified: true,
          //firstName: values.firstName,
          //lastName: values.lastName,
          displayName: values.displayName,
          //photoURL: values.photoURL,
        });

        const userRecord = result.data;
        const userId = userRecord.uid;

        await setRolesOfUser({
          roles: [...values.roles, "MEMBER"],
          userId: userId,
        });

        let hash = "";
        const salt = crypto.randomBytes(16).toString("hex");
        crypto.pbkdf2(
          password,
          salt,
          4300000,
          64,
          "sha512",
          (err, derivedKey) => {
            if (err) throw err;
            hash = derivedKey.toString("hex");
          }
        );

        const createdDoc = await createDoc(
          "users",
          {
            signedinAt: null,
            signedinBy: null,
            detectedAt: null,
            detectedBy: null,
            email: values.email,
            designation: values.designation,
            displayName: values.displayName,
            roles: [...values.roles, "MEMBER"],
            root: false,
            system: false,
            changePasswordRequired: true,
            salt: salt,
            hashes: ["", "", "", "", hash], // initialize with three empty element for the purpose of concat and pop in order to maintain lastest 5 hashes
          },
          user.uid,
          userId
        );

        await logUserActivity({
          uid: user.uid,
          activity: "create user",
          document: createdDoc,
          timestamp: serverTimestamp(),
        });

        sendSignupEmail({
          toEmails: [values.email],
          password: password,
        })
          .then(async () => {
            console.log("update send");
            await updateDoc("users", userId, {
              sentAt: serverTimestamp(),
              sentBy: "system",
            });
            console.log("send updated");
          })
          .catch((error) => {
            throw error;
          });

        dispatchIfNotUnmounted({
          type: "CREATED_USER",
          payload: toPresentationValue(createdDoc.data),
        });
      } else {
        let error = new Error(
          "Permission Denied. You are not allowed to update user."
        );
        error.name = "PermissionDeniedError";
        throw error;
      }
    } catch (err) {
      dispatchError(err);
    }
  };

  return {
    modeTitle,
    modeSubmit,
    modeFieldDisabled,
    modePermission,
    modeValidation,
    submitNew,
    response,
    dispatchDismiss,
    dispatchError,
  };
};
